import { storage } from "@/modules/storage";

/**
 * Shop items reached through an unlisted item's direct link.
 *
 * An unlisted item is deliberately absent from every catalog response, so without this the
 * patient would have to re-open the link every time they browsed away. Remembering the id here
 * lets the shop re-fetch it by id and show it alongside the rest of the catalog for a while.
 *
 * Client-side only and per-device by design: the practice shared a link, not an entitlement, so
 * nothing about who opened it is worth persisting server-side.
 */
export type UnlockedShopItemKind = "listing" | "membership" | "product";

export type UnlockedShopItem = {
  id: string;
  kind: UnlockedShopItemKind;
  /** Epoch ms. Entries past this are dropped on the next read. */
  expiresAt: number;
};

const STORAGE_KEY = "unlockedShopItems";
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Every remembered item costs one by-id request on each shop load. A patient realistically opens a
 * handful of shared links, so this only bounds the pathological case — it is not a product limit.
 * When it bites, the least recently opened entries go first.
 */
const MAX_REMEMBERED = 20;

/**
 * Ids reach the server as a `Guid[]` query parameter. A malformed one makes model binding reject
 * the whole product-listings request, so the shop would lose every product rather than one entry
 * — validate the shape here and drop anything that would not bind.
 */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isUnlockedShopItem = (value: unknown): value is UnlockedShopItem => {
  if (typeof value !== "object" || value === null) return false;
  const entry = value as Partial<UnlockedShopItem>;

  return (
    typeof entry.id === "string" &&
    UUID_PATTERN.test(entry.id) &&
    (entry.kind === "listing" || entry.kind === "membership" || entry.kind === "product") &&
    typeof entry.expiresAt === "number" &&
    Number.isFinite(entry.expiresAt)
  );
};

const read = (): { entries: UnlockedShopItem[]; hadInvalid: boolean } => {
  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) return { entries: [], hadInvalid: false };

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return { entries: [], hadInvalid: true };

    const entries = parsed.filter(isUnlockedShopItem);
    return { entries, hadInvalid: entries.length !== parsed.length };
  } catch {
    // A corrupt entry is not worth surfacing: the worst outcome is that the patient
    // re-opens the link they already have.
    return { entries: [], hadInvalid: true };
  }
};

const write = (entries: UnlockedShopItem[]) => {
  storage.setItem(STORAGE_KEY, JSON.stringify(entries));
};

/**
 * Reads the still-valid unlocked items, pruning anything past its TTL.
 */
export const getUnlockedShopItems = (): UnlockedShopItem[] => {
  const { entries, hadInvalid } = read();
  const live = entries.filter((entry) => entry.expiresAt > Date.now());

  // The cap is enforced on read as well as write. Stored data is untrusted — an oversized array of
  // otherwise-valid entries would otherwise pass straight through into an unbounded by-id fan-out,
  // or a product-list request the server now rejects outright.
  const capped = live.sort((a, b) => a.expiresAt - b.expiresAt).slice(-MAX_REMEMBERED);

  // Prune expired, malformed, and over-cap entries, or the same excess is re-read every load.
  if (hadInvalid || capped.length !== entries.length) {
    write(capped);
  }

  return capped;
};

/**
 * Records an unlisted item the patient just opened, refreshing its expiry so an item they keep
 * coming back to keeps working.
 */
export const rememberUnlockedShopItem = (id: string, kind: UnlockedShopItemKind) => {
  const entries = getUnlockedShopItems().filter((entry) => entry.id !== id);
  const next = [...entries, { id, kind, expiresAt: Date.now() + TTL_MS }];

  // expiresAt only moves forward on re-open, so the newest entries are the ones most recently used.
  write(next.sort((a, b) => a.expiresAt - b.expiresAt).slice(-MAX_REMEMBERED));
};

export const forgetUnlockedShopItem = (id: string) => {
  write(getUnlockedShopItems().filter((entry) => entry.id !== id));
};
